import React, { useCallback, useEffect, useState } from "react";
import { Route as RouteIcon, Search, Clock, LogOut } from "lucide-react";
import { setToken, getToken } from "../software/api";
import { flush, resyncVisits, live, claimCache } from "./techData";
import { Big, C } from "./ui";
import RunScreen from "./RunScreen";
import ClientsScreen from "./ClientsScreen";
import TimesheetScreen from "./TimesheetScreen";
import StopScreen from "./StopScreen";
import TestScreen from "./TestScreen";
import PartsScreen from "./PartsScreen";
import { ChecklistScreen, NotesScreen, PhotosScreen } from "./TaskScreens";
import { PoolScreen } from "./PoolScreen";
import { OnWayScreen, FinishScreen, MessageScreen } from "./SmsScreens";
import HistoryScreen from "./HistoryScreen";
import FolderScreen from "./FolderScreen";
import WoView from "./WoView";
import SaleView from "./SaleView";

const SCREENS = { stop: StopScreen, test: TestScreen, parts: PartsScreen, checklist: ChecklistScreen, notes: NotesScreen, photos: PhotosScreen, pool: PoolScreen, onway: OnWayScreen, finish: FinishScreen, sms: MessageScreen, history: HistoryScreen, folder: FolderScreen, wo: WoView, sale: SaleView };
const TABS = [["run", "My Run", RouteIcon], ["clients", "Clients", Search], ["time", "Timesheet", Clock]];

function Login({ onIn }) {
  const [email, setEmail] = useState(""), [pw, setPw] = useState(""), [err, setErr] = useState(""), [busy, setBusy] = useState(false);
  const go = async (e) => {
    e.preventDefault(); setBusy(true); setErr("");
    try {
      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/login`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ email: email.trim(), password: pw }) });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Wrong email or password");
      setToken(d.token); claimCache(d.user.id); localStorage.setItem("tech:user", JSON.stringify(d.user)); onIn(d.user);
    } catch (e2) { setErr(e2.message); } finally { setBusy(false); }
  };
  const inp = "w-full h-16 rounded-2xl border-2 border-slate-200 px-4 text-[19px] font-bold outline-none focus:border-cyan-500";
  return (
    <form onSubmit={go} className="min-h-[100dvh] flex flex-col justify-center gap-4 p-6" style={{ background: C.navy }} data-testid="tech-login">
      <img src="/glamour-logo.png" alt="Glamour Pools and Spas" className="h-20 object-contain mx-auto mb-2" />
      <div className="text-white text-center text-[26px] font-black">Pool Tech</div>
      <input value={email} onChange={(e) => setEmail(e.target.value)} type="email" autoComplete="username" placeholder="Your email" data-testid="tech-login-email" className={inp} />
      <input value={pw} onChange={(e) => setPw(e.target.value)} type="password" autoComplete="current-password" placeholder="Password" data-testid="tech-login-password" className={inp} />
      {err && <div className="text-red-200 text-[16px] font-bold text-center" data-testid="tech-login-error">{err}</div>}
      <Big tone="aqua" testid="tech-login-submit" disabled={busy}>{busy ? "Signing in…" : "Sign in"}</Big>
    </form>
  );
}

export default function TechApp() {
  const [user, setUser] = useState(() => (getToken() ? JSON.parse(localStorage.getItem("tech:user") || "null") : null));
  const [tab, setTab] = useState("run"), [stack, setStack] = useState([]);
  useEffect(() => {
    document.title = "Pool Tech";
    const l = document.createElement("link"); l.rel = "manifest"; l.href = "/tech-manifest.json"; document.head.appendChild(l);
    const m = document.createElement("meta"); m.name = "apple-mobile-web-app-title"; m.content = "Pool-Tech"; document.head.appendChild(m);
    const a = document.createElement("link"); a.rel = "apple-touch-icon"; a.href = "/tech-icon-192.png"; document.head.appendChild(a);
    if ("serviceWorker" in navigator) { navigator.serviceWorker.register("/tech-sw.js", { scope: "/pool-tech" }).catch(() => {}); navigator.serviceWorker.getRegistrations().then((rs) => rs.forEach((r) => r.scope.endsWith("/tech") && r.unregister())).catch(() => {}); }
    resyncVisits();
    flush();
    return () => { l.remove(); m.remove(); a.remove(); };
  }, []);
  useEffect(() => {
    if (!getToken()) return;
    // the sign-in is shared with the desktop on this phone - always show the person the server says is signed in
    live("/auth/me").then((me) => {
      claimCache(me.id);
      localStorage.setItem("tech:user", JSON.stringify(me));
      setUser((u) => (u && u.id === me.id ? u : me));
    }).catch((e) => { if (/auth|token|401/i.test(e.message)) { setToken(""); localStorage.removeItem("tech:user"); setUser(null); } });
  }, []);
  useEffect(() => {
    const f = () => setStack((s) => s.slice(0, -1));
    window.addEventListener("popstate", f);
    return () => window.removeEventListener("popstate", f);
  }, []);
  const go = useCallback((name, props = {}) => { window.history.pushState({ t: name }, ""); setStack((s) => [...s, { name, props }]); }, []);
  const back = useCallback(() => window.history.back(), []);
  const home = useCallback(() => { const n = stack.length; setStack([]); setTab("run"); if (n) window.history.go(-n); }, [stack.length]);
  if (!user) return <Login onIn={setUser} />;
  const logout = () => { setToken(""); localStorage.removeItem("tech:user"); setUser(null); setStack([]); setTab("run"); };
  const nav = { go, back, home, user };
  const top = stack[stack.length - 1];
  if (top) { const S = SCREENS[top.name]; return <S key={stack.length} {...top.props} nav={nav} />; }
  const Body = { run: RunScreen, clients: ClientsScreen, time: TimesheetScreen }[tab];
  const tabs = ( // header menu: My Run · Clients · Timesheet · Log out
    <nav className="grid grid-cols-4 bg-white" data-testid="tech-tabbar">
      {TABS.map(([k, l, Icon]) => (
        <button key={k} onClick={() => setTab(k)} data-testid={`tech-tab-${k}`} className={`h-[46px] flex flex-col items-center justify-center gap-0.5 text-[10px] font-black ${tab === k ? "text-cyan-600" : "text-slate-500"}`}>
          <Icon className="h-5 w-5" strokeWidth={tab === k ? 2.6 : 2} />{l}
        </button>
      ))}
      <button onClick={() => window.confirm("Log out of Pool-Tech?") && logout()} data-testid="tech-tab-logout" className="h-[46px] flex flex-col items-center justify-center gap-0.5 text-[10px] font-black text-red-600">
        <LogOut className="h-5 w-5" strokeWidth={2} />Log out
      </button>
    </nav>
  );
  return <Body nav={nav} onLogout={logout} tabs={tabs} />;
}
